import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button,
  Paper, InputAdornment, IconButton, CircularProgress, Chip, GlobalStyles,
} from '@mui/material';
import {
  Visibility, VisibilityOff,
  PointOfSale, Inventory2Outlined, LoyaltyOutlined,
  ArrowBack, Lock, Refresh,
  WbSunnyOutlined, LightModeOutlined, WbTwilightOutlined, NightlightOutlined,
} from '@mui/icons-material';
import amzytickLogo from '../assets/amzyticks_logo.png';
import { useLoginMutation, useSelectTerminalMutation } from '../services/authApi';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../store/authSlice';
import { useToast } from '../components/common/ToastProvider';
import { apiErr } from '../utils/apiError';
import AuthLoadingScreen from '../components/common/AuthLoadingScreen';

const ACTIVE_DRAFT_ORDER_ID_KEY = 'activeDraftOrderId';

function getGreeting() {
  const h = new Date().getHours();
  if (h < 5)  return 'Good Night';
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  if (h < 21) return 'Good Evening';
  return 'Good Night';
}

function getTimeIcon() {
  const h = new Date().getHours();
  if (h < 5)  return { Icon: NightlightOutlined,        color: '#818cf8' };
  if (h < 12) return { Icon: WbSunnyOutlined,   color: '#f59e0b' };
  if (h < 17) return { Icon: LightModeOutlined,  color: '#f97316' };
  if (h < 21) return { Icon: WbTwilightOutlined, color: '#a855f7' };
  return       { Icon: NightlightOutlined,        color: '#818cf8' };
}

// color / delay / duration for each animated orb
const ORBS = [
  { s: 520, t: '-12%', l: '-8%',  c: 'rgba(79,70,229,0.28)',  d: '0s',   dur: '14s' },
  { s: 420, t: '55%',  l: '5%',   c: 'rgba(6,182,212,0.2)',   d: '3.5s', dur: '17s' },
  { s: 360, t: '15%',  l: '55%',  c: 'rgba(124,58,237,0.22)', d: '1.5s', dur: '11s' },
  { s: 280, t: '75%',  l: '70%',  c: 'rgba(16,185,129,0.15)', d: '5s',   dur: '20s' },
  { s: 200, t: '40%',  l: '85%',  c: 'rgba(239,68,68,0.12)',  d: '2.5s', dur: '13s' },
];

const FEATURES = [
  { icon: <PointOfSale sx={{ fontSize: 14 }} />,        label: 'Split Payments' },
  { icon: <Inventory2Outlined sx={{ fontSize: 14 }} />, label: 'Inventory Sync' },
  { icon: <LoyaltyOutlined sx={{ fontSize: 14 }} />,    label: 'Loyalty Points' },
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#f8fafc',
    borderRadius: '12px',
    fontSize: '0.9rem',
    transition: 'all 0.25s',
    '& fieldset': { borderColor: '#e2e8f0', transition: 'all 0.25s' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: '#4f46e5', borderWidth: '1.5px' },
    '&.Mui-focused': { bgcolor: '#fafbff', boxShadow: '0 0 0 4px rgba(79,70,229,0.08)' },
  },
  '& .MuiInputLabel-root': { fontSize: '0.875rem', color: '#94a3b8' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4f46e5' },
};

// ── Terminal Card ─────────────────────────────────────────────────────────────
function TerminalCard({ terminal, onSelect, isLoading, selectedId }) {
  const occupied   = terminal.occupied;
  const disabled   = occupied;
  const isSelected = selectedId === terminal.id;

  return (
    <Paper
      onClick={() => !disabled && !isLoading && onSelect(terminal)}
      elevation={0}
      sx={{
        border: disabled
          ? '1px solid #E2E8F0'
          : isSelected
            ? '1.5px solid #4F46E5'
            : '1px solid #E2E8F0',
        borderLeft: disabled ? '3px solid #CBD5E1' : '3px solid #4F46E5',
        borderRadius: '10px',
        p: 1.5,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        bgcolor: isSelected ? '#EEF2FF' : '#fff',
        transition: 'all 0.15s ease',
        position: 'relative',
        '&:hover': disabled || isLoading ? {} : {
          borderColor: '#4F46E5',
          boxShadow: '0 2px 10px rgba(79,70,229,0.12)',
        },
      }}
    >
      {isSelected && isLoading && (
        <Box sx={{
          position: 'absolute', inset: 0, borderRadius: '10px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(255,255,255,0.75)',
        }}>
          <CircularProgress size={22} sx={{ color: '#4F46E5' }} />
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
        <Chip
          label={terminal.terminal_number}
          size="small"
          sx={{
            fontSize: '0.6rem', fontWeight: 700, height: 20,
            bgcolor: occupied ? '#F1F5F9' : '#EEF2FF',
            color: occupied ? '#94A3B8' : '#4F46E5',
            borderRadius: '5px', letterSpacing: '0.04em',
          }}
        />
        {occupied ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Lock sx={{ fontSize: 11, color: '#94A3B8' }} />
            <Typography sx={{ fontSize: '0.6rem', color: '#94A3B8', fontWeight: 600 }}>In Session</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#16a34a' }} />
            <Typography sx={{ fontSize: '0.6rem', color: '#16a34a', fontWeight: 600 }}>Available</Typography>
          </Box>
        )}
      </Box>

      <Typography sx={{
        fontSize: '0.82rem', fontWeight: 700,
        color: disabled ? '#94A3B8' : '#1E293B',
        lineHeight: 1.3,
      }}>
        {terminal.name}
      </Typography>
    </Paper>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const LoginPage = () => {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const showToast = useToast();

  // Step 1 state
  const [step, setStep]               = useState(1);
  const [organization_code, setOrganizationCode] = useState('');
  const [phone_number, setPhoneNumber] = useState('');
  const [password, setPassword]       = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Phone number is only unique per organization, so it must stay strictly
  // 10 digits — never let it grow past that or accept non-numeric input.
  const handlePhoneNumberChange = (e) => {
    setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10));
  };

  // Step 2 state
  const [terminals, setTerminals]       = useState([]);
  const [stepOneUser, setStepOneUser]   = useState(null);
  const [stepOneStore, setStepOneStore] = useState(null);
  const [selectedTerminalId, setSelectedTerminalId] = useState(null);
  const [isAuthTransitioning, setIsAuthTransitioning] = useState(false);

  const [login,          { isLoading: isLoggingIn }]        = useLoginMutation();
  const [selectTerminal, { isLoading: isSelectingTerminal }] = useSelectTerminalMutation();

  // Typewriter effect for greeting (step 1 only)
  const fullGreeting = getGreeting();
  const timeIcon     = getTimeIcon();
  const [typedText,  setTypedText]  = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [showIcon,   setShowIcon]   = useState(false);

  useEffect(() => {
    if (step !== 1) return;
    setTypedText('');
    setShowCursor(true);
    setShowIcon(false);
    const chars = Array.from(fullGreeting);
    let i = 0;
    const timer = setInterval(() => {
      i++;
      setTypedText(chars.slice(0, i).join(''));
      if (i >= chars.length) {
        clearInterval(timer);
        setTimeout(() => { setShowCursor(false); setShowIcon(true); }, 900);
      }
    }, 65);
    return () => { clearInterval(timer); };
  }, [step]);

  // ── Step 1: credential check ──────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!organization_code || !phone_number || !password) {
      showToast('Please enter organization code, phone number, and password', 'warning');
      return;
    }
    if (phone_number.length !== 10) {
      showToast('Phone number must be exactly 10 digits', 'warning');
      return;
    }
    try {
      const res = await login({ organization_code, phone_number, password }).unwrap();
      const { user, store, terminals: terminalList } = res.data;
      setStepOneUser(user);
      setStepOneStore(store);
      setTerminals(terminalList || []);
      setStep(2);
    } catch (err) {
      showToast(apiErr(err, 'Login failed. Please check your credentials.'), 'error');
    }
  };

  // ── Step 2: terminal selection ────────────────────────────────────────────
  const handleSelectTerminal = async (terminal) => {
    setSelectedTerminalId(terminal.id);
    setIsAuthTransitioning(true);
    try {
      const res = await selectTerminal({ organization_code, phone_number, password, terminal_id: terminal.id }).unwrap();
      const { user, store, organization, terminal: selectedTerminal, access_token, refresh_token } = res.data;
      dispatch(setCredentials({ user, store, organization, terminal: selectedTerminal, access_token, refresh_token }));
      sessionStorage.removeItem(ACTIVE_DRAFT_ORDER_ID_KEY);
      showToast('Login successful', 'success');
      setTimeout(() => navigate('/dashboard', { replace: true }), 1000);
    } catch (err) {
      setSelectedTerminalId(null);
      setIsAuthTransitioning(false);
      const status = err.status;
      if (status === 403) {
        showToast('This terminal is currently in use. Please choose another.', 'error');
      } else if (status === 422) {
        showToast(apiErr(err, 'Terminal unavailable.'), 'error');
      } else {
        showToast(apiErr(err, 'Failed to connect to terminal.'), 'error');
      }
    }
  };

  // ── Step 2: refresh terminal availability ─────────────────────────────────
  const handleRefreshTerminals = async () => {
    try {
      const res = await login({ organization_code, phone_number, password }).unwrap();
      const { user, store, terminals: terminalList } = res.data;
      setStepOneUser(user);
      setStepOneStore(store);
      setTerminals(terminalList || []);
    } catch (err) {
      showToast(apiErr(err, 'Failed to refresh terminals.'), 'error');
    }
  };

  const handleBack = () => {
    setStep(1);
    setTerminals([]);
    setStepOneUser(null);
    setStepOneStore(null);
    setSelectedTerminalId(null);
    setIsAuthTransitioning(false);
  };

  // ── Step 1 Card ───────────────────────────────────────────────────────────
  const StepOneCard = (
    <Box sx={{ width: '100%', maxWidth: 400, animation: 'cardEnter 0.8s 0.2s cubic-bezier(0.16,1,0.3,1) both' }}>
      <Box sx={{
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '20px',
        p: { xs: '28px 24px', sm: '36px 40px' },
        boxShadow: {
          xs: '0 8px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.08)',
          md: '0 4px 6px rgba(0,0,0,0.04), 0 20px 60px rgba(79,70,229,0.08)',
        },
      }}>
        {/* Logo — mobile only */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 1.5 }}>
          <img src={amzytickLogo} alt="Amzyticks" style={{ width: 85, height: 'auto', display: 'block' }} />
        </Box>

        {/* Typewriter greeting */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.5, minHeight: '2.6rem' }}>
          <Typography sx={{ fontSize: '1.7rem', fontWeight: 800, color: '#1e293b', letterSpacing: '-0.02em' }}>
            {typedText}
            {showCursor && (
              <Box component="span" sx={{
                display: 'inline-block', width: '2px', height: '1.6rem',
                bgcolor: '#4f46e5', ml: '2px', mb: '-3px', borderRadius: '1px',
                animation: 'blink 0.75s step-end infinite',
              }} />
            )}
          </Typography>
          {showIcon && (
            <timeIcon.Icon sx={{
              fontSize: '1.9rem', color: timeIcon.color,
              animation: 'iconPop 0.5s cubic-bezier(0.16,1,0.3,1) both',
              filter: `drop-shadow(0 0 8px ${timeIcon.color}88)`,
            }} />
          )}
        </Box>
        <Typography sx={{ color: '#64748b', fontSize: '0.83rem', mb: 3.5, lineHeight: 1.5 }}>
          Sign in to your POS Terminal
        </Typography>

        <Box component="form" onSubmit={handleLogin} autoComplete="on"
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          <TextField
            fullWidth label="Organization Code" variant="outlined"
            autoComplete="organization"
            value={organization_code} onChange={e => setOrganizationCode(e.target.value)}
            disabled={isLoggingIn} sx={inputSx}
          />

          <TextField
            fullWidth label="Phone Number" variant="outlined"
            autoComplete="username"
            value={phone_number} onChange={handlePhoneNumberChange}
            disabled={isLoggingIn} sx={inputSx}
            inputProps={{ inputMode: 'numeric', maxLength: 10 }}
          />

          <TextField
            fullWidth label="Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password" variant="outlined"
            value={password} onChange={e => setPassword(e.target.value)}
            disabled={isLoggingIn}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(v => !v)} edge="end" size="small" tabIndex={-1}
                    sx={{ color: '#94a3b8', transition: 'color 0.2s', '&:hover': { color: '#4f46e5' } }}>
                    {showPassword
                      ? <VisibilityOff sx={{ fontSize: 19 }} />
                      : <Visibility   sx={{ fontSize: 19 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />

          <Button
            fullWidth type="submit" variant="contained"
            disabled={isLoggingIn} disableElevation
            sx={{
              mt: 0.5, py: 1.5,
              background: 'linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)',
              color: '#fff',
              fontSize: '0.95rem', fontWeight: 700,
              borderRadius: '12px', textTransform: 'none', letterSpacing: '0.02em',
              transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
              boxShadow: '0 4px 28px rgba(79,70,229,0.4)',
              position: 'relative', overflow: 'hidden',
              '&::before': {
                content: '""', position: 'absolute', inset: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%)',
                borderRadius: 'inherit',
              },
              '&:hover:not(.Mui-disabled)': {
                boxShadow: '0 8px 40px rgba(79,70,229,0.55)',
                transform: 'translateY(-2px)',
                background: 'linear-gradient(135deg, #4338ca 0%, #0284c7 100%)',
              },
              '&:active:not(.Mui-disabled)': { transform: 'translateY(0)', boxShadow: '0 4px 20px rgba(79,70,229,0.35)' },
              '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8' },
            }}
          >
            {isLoggingIn ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  {[0, 1, 2].map(i => (
                    <Box key={i} sx={{
                      width: 5, height: 5, borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.5)',
                      animation: 'dotPulse 1s ease-in-out infinite',
                      animationDelay: `${i * 0.18}s`,
                    }} />
                  ))}
                </Box>
                Verifying Identity
              </Box>
            ) : 'Sign In →'}
          </Button>
        </Box>

        <Box sx={{ mt: 3.5, pt: 2.5, borderTop: '1px solid #cbd5e1', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.67rem', color: '#94a3b8', letterSpacing: '0.05em', fontWeight: 600 }}>
            SECURED BY AMZYTICKS
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // ── Step 2 Card ───────────────────────────────────────────────────────────
  const StepTwoCard = (
    <Box sx={{ width: '100%', maxWidth: 440, animation: 'cardEnter 0.8s 0.1s cubic-bezier(0.16,1,0.3,1) both' }}>
      <Box sx={{
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '20px',
        p: { xs: '28px 24px', sm: '36px 40px' },
        boxShadow: {
          xs: '0 8px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.08)',
          md: '0 4px 6px rgba(0,0,0,0.04), 0 20px 60px rgba(79,70,229,0.08)',
        },
      }}>
        {/* Back button + refresh */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Button
            startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
            onClick={handleBack}
            disabled={isSelectingTerminal}
            size="small"
            sx={{
              textTransform: 'none', color: '#64748b', fontSize: '0.78rem',
              fontWeight: 600, p: 0, minWidth: 0,
              '&:hover': { bgcolor: 'transparent', color: '#4f46e5' },
            }}
          >
            Change Account
          </Button>

          <IconButton
            onClick={handleRefreshTerminals}
            disabled={isLoggingIn || isSelectingTerminal}
            size="small"
            title="Refresh terminal availability"
            sx={{
              color: '#94a3b8', transition: 'color 0.2s',
              '&:hover': { color: '#4f46e5', bgcolor: 'rgba(79,70,229,0.06)' },
            }}
          >
            <Refresh sx={{
              fontSize: 18,
              animation: isLoggingIn ? 'spin 0.8s linear infinite' : 'none',
            }} />
          </IconButton>
        </Box>

        {/* User + store info */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}>
            Select Terminal
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#64748b', mt: 0.4 }}>
            {stepOneUser?.name} &middot; {stepOneStore?.name}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.2 }}>
            Choose your billing counter to begin
          </Typography>
        </Box>

        {/* Terminal cards */}
        {terminals.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <PointOfSale sx={{ fontSize: 40, color: '#CBD5E1', mb: 1.5 }} />
            <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: 500 }}>
              No terminals configured for this store.
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#CBD5E1', mt: 0.5 }}>
              Contact your admin to set up terminals.
            </Typography>
          </Box>
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: terminals.length === 1 ? '1fr' : 'repeat(2, 1fr)',
            },
            gap: 1.5,
          }}>
            {terminals.map(terminal => (
              <TerminalCard
                key={terminal.id}
                terminal={terminal}
                onSelect={handleSelectTerminal}
                isLoading={isSelectingTerminal}
                selectedId={selectedTerminalId}
              />
            ))}
          </Box>
        )}

        {terminals.some(t => t.occupied) && (
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mt: 2, textAlign: 'center' }}>
            Greyed out terminals are occupied. Ask your manager to unblock.
          </Typography>
        )}
      </Box>
    </Box>
  );

  // ── Auth transition screen ────────────────────────────────────────────────
  if (isAuthTransitioning) {
    return (
      <AuthLoadingScreen
        title="Signing you in..."
        subtitle="Preparing your billing terminal"
      />
    );
  }

  // ── Page layout ───────────────────────────────────────────────────────────
  return (
    <>
      <GlobalStyles styles={{
        '@keyframes orbDrift': {
          '0%,100%': { transform: 'translate(0,0) scale(1)' },
          '25%':     { transform: 'translate(40px,-30px) scale(1.06)' },
          '50%':     { transform: 'translate(20px, 40px) scale(0.96)' },
          '75%':     { transform: 'translate(-30px,10px) scale(1.03)' },
        },
        '@keyframes cardEnter': {
          from: { opacity: 0, transform: 'translateY(32px) scale(0.97)' },
          to:   { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
        '@keyframes fadeSlideLeft': {
          from: { opacity: 0, transform: 'translateX(-20px)' },
          to:   { opacity: 1, transform: 'translateX(0)' },
        },
        '@keyframes dotPulse': {
          '0%,100%': { opacity: 0.4 },
          '50%': { opacity: 1 },
        },
        '@keyframes blink': {
          '0%,100%': { opacity: 1 },
          '50%': { opacity: 0 },
        },
        '@keyframes shimmer': {
          '0%':   { backgroundPosition: '-200% center' },
          '100%': { backgroundPosition:  '200% center' },
        },
        '@keyframes iconPop': {
          '0%':   { opacity: 0, transform: 'scale(0.3) rotate(-20deg)' },
          '70%':  { transform: 'scale(1.2) rotate(8deg)' },
          '100%': { opacity: 1, transform: 'scale(1) rotate(0deg)' },
        },
        '@keyframes spin': {
          from: { transform: 'rotate(0deg)' },
          to:   { transform: 'rotate(360deg)' },
        },
      }} />

      <Box sx={{
        height: '100vh', overflow: 'hidden', display: 'flex',
        background: 'linear-gradient(135deg, #08091f 0%, #0d1130 45%, #0a1628 100%)',
        position: 'relative',
      }}>

        {/* Animated orbs */}
        {ORBS.map((o, i) => (
          <Box key={i} sx={{
            position: 'absolute', width: o.s, height: o.s,
            top: o.t, left: o.l, borderRadius: '50%',
            bgcolor: o.c, filter: 'blur(90px)',
            animation: `orbDrift ${o.dur} ease-in-out infinite`,
            animationDelay: o.d, pointerEvents: 'none',
          }} />
        ))}

        {/* Dot-grid overlay */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)',
          backgroundSize: '36px 36px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, #000 40%, transparent 100%)',
        }} />

        {/* Scan line */}
        <Box sx={{
          position: 'absolute', left: 0, right: 0, height: '1px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(6,182,212,0.35) 50%, transparent 100%)',
          top: '45%', pointerEvents: 'none', zIndex: 0,
          animation: 'dotPulse 4s ease-in-out infinite',
        }} />

        {/* Left brand panel — desktop only */}
        <Box sx={{
          display: { xs: 'none', md: 'flex' },
          width: '46%', flexShrink: 0,
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          position: 'relative', zIndex: 1, px: 6,
        }}>
          {/* Logo card */}
          <Box sx={{
            bgcolor: '#fff', borderRadius: '14px', px: 3, py: 1.8,
            boxShadow: '0 0 0 1px rgba(255,255,255,0.08), 0 20px 60px rgba(0,0,0,0.5), 0 0 80px rgba(79,70,229,0.25)',
            mb: 4,
            animation: 'cardEnter 0.8s cubic-bezier(0.16,1,0.3,1) both',
            animationDelay: '0.05s',
          }}>
            <img src={amzytickLogo} alt="Amzyticks" style={{ width: 140, height: 'auto', display: 'block' }} />
          </Box>

          <Box sx={{
            textAlign: 'center', maxWidth: 320,
            animation: 'cardEnter 0.8s 0.15s cubic-bezier(0.16,1,0.3,1) both',
          }}>
            <Typography sx={{
              color: 'rgba(255,255,255,0.28)', fontSize: '0.6rem', fontWeight: 700,
              letterSpacing: '0.28em', textTransform: 'uppercase', mb: 1.5,
            }}>
              Point of Sale Terminal
            </Typography>

            <Typography sx={{
              fontSize: { md: '1.45rem', lg: '1.65rem' }, fontWeight: 800, lineHeight: 1.3, mb: 2,
              background: 'linear-gradient(135deg, #f1f5f9 20%, #06b6d4 70%, #7c3aed 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              animation: 'shimmer 4s linear infinite',
            }}>
              Fast, Reliable Billing for Your Retail Operations
            </Typography>

            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', lineHeight: 1.8, mb: 4 }}>
              Manage sales, inventory sync, and loyalty from one unified terminal.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
              {FEATURES.map((f, i) => (
                <Box key={f.label} sx={{
                  display: 'flex', alignItems: 'center', gap: 0.7,
                  bgcolor: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '20px', px: 1.75, py: 0.65,
                  color: 'rgba(255,255,255,0.65)', fontSize: '0.68rem', fontWeight: 600,
                  backdropFilter: 'blur(8px)',
                  cursor: 'default',
                  animation: `fadeSlideLeft 0.5s ${0.5 + i * 0.1}s both`,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(6,182,212,0.12)',
                    borderColor: 'rgba(6,182,212,0.35)',
                    color: '#67e8f9',
                  },
                }}>
                  {f.icon}{f.label}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Right form panel */}
        <Box sx={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 },
          bgcolor: { xs: 'transparent', md: '#f1f5f9' },
          overflowY: 'auto',
        }}>
          {step === 1 ? StepOneCard : StepTwoCard}
        </Box>

      </Box>
    </>
  );
};

export default LoginPage;
